export const SUPPORT_STACK_OPERATING_MODES = [
    "ACTUALS",
    "BUDGETS",
    "FORECASTS",
];
export const SUPPORT_STACK_MODULES = [
    {
        id: "accruals",
        label: "Accruals",
        perspective: "trackers",
        route: "/reports/adjusting-workpaper",
        authority: "posted-actuals-only",
        coverage: "partial",
        currentModels: ["AdjustingEntry", "JournalEntry", "Tracker"],
        lensUse: "Watch period-end obligations and reversal readiness.",
        operatingSpine: {
            ACTUALS: "Period-end accrual and reversal JEs.",
            BUDGETS: "Expected obligations that should be accrued by period.",
            FORECASTS: "Run-rate obligations and reversal timing assumptions.",
        },
        actualsBoundary: "Actual only when the accrual JE is posted.",
        gate: "Recurring accrual schedules need policy and generated-entry review before schema expansion.",
    },
    {
        id: "prepayments",
        label: "Prepayments",
        perspective: "schedules",
        route: "/schedules",
        authority: "schedule-support",
        coverage: "implemented",
        currentModels: ["AmortizationContract(PREPAYMENT)", "AmortizationScheduleRow", "JournalEntry"],
        lensUse: "Spread prepaid cost into the right periods.",
        operatingSpine: {
            ACTUALS: "Posted amortization expense and remaining prepaid balance.",
            BUDGETS: "Budgeted amortization recognition by period.",
            FORECASTS: "Forward prepaid runoff and renewal timing.",
        },
        actualsBoundary: "Actual only when amortization is posted through a JE.",
        gate: "Keep prepaid schedules reconciled to GL carrying accounts.",
    },
    {
        id: "deferrals",
        label: "Deferrals",
        perspective: "schedules",
        route: "/schedules",
        authority: "schedule-support",
        coverage: "future-gated",
        currentModels: ["JournalEntry"],
        lensUse: "Hold deferred revenue or expense until recognition timing is approved.",
        operatingSpine: {
            ACTUALS: "Posted recognition from deferred revenue or expense.",
            BUDGETS: "Planned recognition releases for committed deferrals.",
            FORECASTS: "Scenario timing for recognition and release curves.",
        },
        actualsBoundary: "Actual only when deferred revenue or expense is recognized through a JE.",
        gate: "Needs liability-side recognition policy before adding schema discriminators.",
    },
    {
        id: "depreciation",
        label: "Depreciation",
        perspective: "subLedgers",
        route: "/fixed-assets",
        authority: "posted-actuals-only",
        coverage: "implemented",
        currentModels: ["FixedAsset", "RecurringTransaction", "JournalEntry", "AmortizationContract(FIXED_ASSET)"],
        lensUse: "Explain fixed-asset carrying value and period expense.",
        operatingSpine: {
            ACTUALS: "Posted depreciation expense and accumulated depreciation.",
            BUDGETS: "Planned depreciation expense for existing asset base.",
            FORECASTS: "Projected asset additions, disposals, and depreciation curves.",
        },
        actualsBoundary: "Actual only when the depreciation JE is posted.",
        gate: "Unified schedules must preserve fixed-asset lifecycle and disposal behavior.",
    },
    {
        id: "amortization",
        label: "Amortization",
        perspective: "schedules",
        route: "/schedules",
        authority: "schedule-support",
        coverage: "implemented",
        currentModels: ["AmortizationContract", "AmortizationScheduleRow", "JournalEntry"],
        lensUse: "Explain intangible, loan, or prepaid amortization timing.",
        operatingSpine: {
            ACTUALS: "Posted amortization entries and carrying-balance movement.",
            BUDGETS: "Planned amortization expense by contract and period.",
            FORECASTS: "Scenario runoff, refinancing, or useful-life assumptions.",
        },
        actualsBoundary: "Schedule rows explain the period plan; JEs remain accounting truth.",
        gate: "Generation remains held until posting and reversal semantics are explicit.",
    },
    {
        id: "loan-schedule",
        label: "Loan Schedule",
        perspective: "subLedgers",
        route: "/sub-ledgers",
        authority: "schedule-support",
        coverage: "implemented",
        currentModels: ["AmortizationContract(LOAN)", "AmortizationScheduleRow", "JournalEntry"],
        lensUse: "Separate principal, interest, fees, and outstanding balance.",
        operatingSpine: {
            ACTUALS: "Posted principal, interest, fee, and balance movement.",
            BUDGETS: "Committed debt-service budget by period.",
            FORECASTS: "Debt-service runway, refinancing, and covenant timing.",
        },
        actualsBoundary: "Actual principal and interest movement requires payment or JE posting.",
        gate: "Do not infer debt truth from a schedule without GL reconciliation.",
    },
    {
        id: "appreciation",
        label: "Appreciation",
        perspective: "schedules",
        route: "/schedules",
        authority: "valuation-policy-gated",
        coverage: "policy-gated",
        currentModels: ["JournalEntry"],
        lensUse: "Stage fair-value uplift scenarios before policy approval.",
        operatingSpine: {
            ACTUALS: "Only approved and posted revaluation movement.",
            BUDGETS: "Usually held out unless policy permits planned uplift.",
            FORECASTS: "Scenario-only valuation sensitivity before approval.",
        },
        actualsBoundary: "No gain or uplift is actual until approved valuation policy posts it.",
        gate: "Needs revaluation/fair-value policy before durable model or report treatment.",
    },
    {
        id: "biological-assets",
        label: "Biological Assets",
        perspective: "schedules",
        route: "/schedules",
        authority: "valuation-policy-gated",
        coverage: "policy-gated",
        currentModels: ["JournalEntry"],
        lensUse: "Hold biological fair-value movement behind valuation policy.",
        operatingSpine: {
            ACTUALS: "Posted fair-value movement after valuation approval.",
            BUDGETS: "Policy-held unless operations budgets need expected yield.",
            FORECASTS: "Scenario valuation and biological transformation assumptions.",
        },
        actualsBoundary: "Fair-value movement needs approved valuation entry and disclosure treatment.",
        gate: "Needs IAS 41-style policy packet before schema or report expansion.",
    },
    {
        id: "intangible-assets",
        label: "Intangible Assets",
        perspective: "subLedgers",
        route: "/sub-ledgers",
        authority: "schedule-support",
        coverage: "implemented",
        currentModels: ["AmortizationContract(INTANGIBLE)", "AmortizationScheduleRow", "JournalEntry"],
        lensUse: "Track amortizable cost, useful life, and impairment checkpoints.",
        operatingSpine: {
            ACTUALS: "Posted amortization, impairment, and carrying value.",
            BUDGETS: "Planned amortization and impairment review cadence.",
            FORECASTS: "Useful-life, renewal, impairment, and growth assumptions.",
        },
        actualsBoundary: "Actual amortization or impairment requires JE posting.",
        gate: "Impairment and indefinite-life treatment need separate policy gates.",
    },
];
const PERSPECTIVE_FAMILY_ID = {
    subLedgers: "sub-ledgers",
    schedules: "schedules",
    trackers: "trackers",
};
export function supportStackFamilyId(module) {
    return PERSPECTIVE_FAMILY_ID[module.perspective];
}
export function modulesForPerspective(perspective) {
    return SUPPORT_STACK_MODULES.filter((module) => module.perspective === perspective);
}
export function moduleOperatingModes(module) {
    return SUPPORT_STACK_OPERATING_MODES.map((mode) => ({
        mode,
        role: module.operatingSpine[mode],
    }));
}
//# sourceMappingURL=support-stack.js.map